// Pins.js
import { generateKey } from './Keys';
import { buildUserSummary } from './Users';

export function getLocationsPhrase(pins, defaultPhrase) {
    // Extract location names from the pins array
    const locations = pins.map(pin => {
        // Check if pin has address details
        if (pin.streetNumber && pin.route) {
            const addressParts = [pin.streetNumber, pin.route];
            if (pin.subpremise) {
                addressParts.push(pin.subpremise);
            }
            return `${addressParts.join(' ')} in ${pin.city}`;
        }
        // Fallback to title, pin, or key
        if (pin.title) return pin.title;
        if (pin.pin) return pin.pin;
        return pin.key;
    });

    // Handle different cases based on array length
    if (locations.length === 0 && defaultPhrase) return defaultPhrase;
    if (locations.length === 1) return `${locations[0]}`;
    if (locations.length === 2) return `${locations[0]} and ${locations[1]}`;
    if (locations.length === 3) return `${locations[0]}, ${locations[1]} and ${locations[2]}`;
    return `${locations[0]}, ${locations[1]} and ${locations.length - 2} other locations`;
}

export const processLocationSearchResult = async (location, user) => {
    // Initialize data fields
    let streetNumber = '';
    let route = '';
    let subpremise = '';
    let city = '';
    let county = '';
    let state = '';
    let country = '';
    let zip = '';
    let title = '';
    let titleFull = '';
    let latitude = null;
    let longitude = null;
    let placeId = '';
    let north = null;
    let south = null;
    let east = null;
    let west = null;
    let width = null;
    let height = null;

    // Extract data from address_components
    if (location?.address_components) {
        location.address_components.forEach((component) => {
            const types = component.types;
            const longName = component.long_name || '';

            // Append the long name of each address component to the full title
            titleFull += (titleFull ? ', ' : '') + longName;

            if (types.includes('locality')) {
                city = longName;
                if (!title) title = longName; // Prefer locality as title
            } else if (types.includes('administrative_area_level_2')) {
                county = longName;
                if (!title) title = longName; // County if no locality
            } else if (types.includes('administrative_area_level_1')) {
                state = component.short_name || '';
                if (!title) title = longName; // State if no locality or county
            } else if (types.includes('country')) {
                country = component.short_name || '';
                if (!title) title = longName; // Country if nothing else
            } else if (types.includes('nodeal_code')) {
                zip = longName;
            } else if (types.includes('street_number')) {
                streetNumber = longName;
            } else if (types.includes('route')) {
                route = longName;
            } else if (types.includes('subpremise')) {
                subpremise = longName;
            }
        });
    }

    // Extract latitude and longitude
    if (location?.geometry?.location) {
        latitude = typeof location.geometry.location.lat === 'function'
            ? location.geometry.location.lat()
            : location.geometry.location.lat ?? null;
        longitude = typeof location.geometry.location.lng === 'function'
            ? location.geometry.location.lng()
            : location.geometry.location.lng ?? null;
    }

    // Extract placeId
    placeId = location?.place_id || '';

    // Extract bounding box coordinates
    let bounds = location?.geometry?.bounds || location?.geometry?.viewport;
    if (bounds) {
        // Handle case where bounds is a LatLngBounds object (Google Maps JS API)
        if (typeof bounds.getNorthEast === 'function') {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            north = ne.lat();
            east = ne.lng();
            south = sw.lat();
            west = sw.lng();
        } else {
            // Handle plain object
            north = bounds.north ?? null;
            south = bounds.south ?? null;
            east = bounds.east ?? null;
            west = bounds.west ?? null;
        }
    }

    // Calculate width and height in miles (approximate)
    if (north != null && south != null && east != null && west != null) {
        const toRadians = (deg) => deg * Math.PI / 180;
        const earthRadius = 3958.8; // Earth's radius in miles

        // Width: distance between west and east at average latitude
        const avgLat = (north + south) / 2;
        const dLonWidth = toRadians(east - west);
        const latRad = toRadians(avgLat);
        width = earthRadius * dLonWidth * Math.cos(latRad);

        // Height: distance between south and north
        const dLatHeight = toRadians(north - south);
        height = earthRadius * dLatHeight;

        // Ensure positive values
        width = Math.abs(width);
        height = Math.abs(height);
    }

    // Generate a unique key
    const key = generateKey();

    // Prepare the data object, ensuring no undefined values
    const data = {
        key: key || '',
        userKey: user?.key || '',
        user: buildUserSummary(user) || null,
        streetNumber: streetNumber || '',
        route: route || '',
        subpremise: subpremise || '',
        city: city || '',
        county: county || '',
        state: state || '',
        country: country || '',
        zip: zip || '',
        latitude: latitude ?? null,
        longitude: longitude ?? null,
        placeId: placeId || '',
        title: title || '',
        titleFull: titleFull || '',
        north: north ?? null,
        south: south ?? null,
        east: east ?? null,
        west: west ?? null,
        width: width ?? null,
        height: height ?? null,
    };

    // Return the location data regardless
    return data;
};

/**
 * Function to format bounding box dimensions.
 */
export const formatBoundingBox = (width, height) => {
    // Helper to format a single dimension
    const formatDimension = (value) => {
        if (value < 1) {
            // Convert miles to feet for small distances
            const feet = Math.round(value * 5280); // 1 mile = 5280 feet
            return feet.toLocaleString() + ' feet';
        } else {
            // Round to 1 decimal place and use commas for thousands
            return value.toLocaleString('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }) + ' miles';
        }
    };

    return `${formatDimension(width)} x ${formatDimension(height)}`;
};

